class SlideshowSection extends HTMLElement {
  constructor() {
    super();
    this.mainSlide = this.querySelector(".swiper-slideshow-main");
    this.videoControl = null;
    this.init();
  }

  init() {
    this.initSlideShow();
    this.initVideoControl();
  }

  initVideoControl() {
    if (!this.querySelector("video-local-slide")) return;

    this.videoControl = this.querySelector("[data-video-control]");
    if (!this.videoControl) {
      this.videoControl = document.createElement("button");
      this.videoControl.type = "button";
      this.videoControl.className = "slideshow-video-control play-button";
      this.videoControl.dataset.videoControl = "";
      this.videoControl.hidden = true;
      this.videoControl.innerHTML = `
        <svg width="10" height="14" viewBox="0 0 10 14" fill="none" class="icon icon-play color-dark" aria-hidden="true">
          <path d="M9.59726 6.19752L1.54253 0.190726C1.24693 -0.0290999 0.854524 -0.0622065 0.530528 0.108622C0.20395 0.278126 0 0.62243 0 0.994544V13.0042C0 13.3803 0.20395 13.7232 0.530528 13.8927C0.668646 13.9642 0.819672 14 0.971989 14C1.17078 14 1.37214 13.9351 1.54253 13.8067L9.59726 7.80516C9.85155 7.61314 9.99999 7.31651 9.99999 7.00134C10.0013 6.68087 9.84897 6.38556 9.59726 6.19752Z" fill="currentColor"/>
        </svg>
        <svg class="icon icon-pause color-dark" width="10" height="14" viewBox="0 0 10 14" fill="none" aria-hidden="true">
          <rect width="2" height="14" fill="currentColor" rx="1"/><rect width="2" height="14" x="8" fill="currentColor" rx="1"/>
        </svg>`;
      this.appendChild(this.videoControl);
    }

    this.videoControl.addEventListener("click", async () => {
      const video = this.getActiveVideo();
      if (!video) return;

      if (video.paused) {
        this.dataset.videoUserPaused = "false";
        try {
          await video.play();
          this.updateVideoControl(false);
        } catch (error) {
          this.dataset.videoUserPaused = "true";
          this.updateVideoControl(true);
          if (error?.name !== "AbortError") {
            console.warn("play failed:", error);
          }
        }
      } else {
        this.dataset.videoUserPaused = "true";
        video.pause();
        this.updateVideoControl(true);
      }
    });

    this.addEventListener(
      "play",
      (event) => {
        if (event.target === this.getActiveVideo()) {
          this.updateVideoControl(false);
        }
      },
      true,
    );

    this.addEventListener(
      "pause",
      (event) => {
        if (event.target === this.getActiveVideo()) {
          this.updateVideoControl(true);
        }
      },
      true,
    );

    this.updateVideoControl();
  }

  getActiveVideo() {
    const activeSlide =
      this.mainSlide?.querySelector(".swiper-slide-active") || this.mainSlide;
    if (!activeSlide) return null;

    const visibleVideoWrapper = [...activeSlide.querySelectorAll("video-local-slide")].find(
      (wrapper) => window.getComputedStyle(wrapper).display !== "none",
    );

    return visibleVideoWrapper?.querySelector("video") || activeSlide.querySelector("video");
  }

  updateVideoControl(paused) {
    if (!this.videoControl) return;

    const video = this.getActiveVideo();
    this.videoControl.hidden = !video;
    if (!video) return;

    const isPaused = typeof paused === "boolean" ? paused : video.paused;
    this.videoControl.classList.toggle("active", !isPaused);
    this.videoControl.setAttribute("aria-pressed", String(isPaused));
    this.videoControl.setAttribute(
      "aria-label",
      isPaused ? "Play background video" : "Pause background video",
    );
    this.videoControl.setAttribute(
      "title",
      isPaused ? "Play background video" : "Pause background video",
    );
  }

  initSlideShow() {
    let autoplay = this.mainSlide?.dataset.autoplay === "true";
    const loop = this.mainSlide?.dataset.loop === "true";
    const autoPlaySpeed = this.mainSlide?.dataset.autoPlaySpeed
      ? this.mainSlide.dataset.autoPlaySpeed
      : 3000;
    const effect = this.mainSlide?.dataset.effect
      ? this.mainSlide.dataset.effect
      : "slide";
    const pauseOnMouseEnter =
      this.mainSlide?.dataset.pauseOnMouseEnter === "true";

    if (autoplay) {
      autoplay = { delay: autoPlaySpeed, pauseOnMouseEnter };
    }

    const _this = this;
    const base = {
      autoplay,
      effect,
      loop,
      navigation: {
        nextEl: this.querySelector(".swiper-button-next"),
        prevEl: this.querySelector(".swiper-button-prev"),
      },
      pagination: {
        clickable: true,
        el: this.querySelector(".swiper-pagination"),
      },
      on: {
        init: function () {
          _this.querySelectorAll(".slide-video-1").forEach((video) => {
            _this.loadSlideVideo(video, false);
          });
          _this.updateVideoControl();
        },
        slideChangeTransitionStart: function () {
          const currentSlide = this.slides[this.activeIndex];
          currentSlide
            .querySelectorAll("motion-effect")
            .forEach(async (motion) => {
              await motion.initAnimate();
            });
        },
        slideChangeTransitionEnd: function () {
          _this.querySelectorAll("video-local-slide").forEach((video) => {
            _this.loadSlideVideo(video, false);
          });
          const currentSlide = this.slides[this.activeIndex];
          currentSlide
            .querySelectorAll("motion-effect")
            .forEach(async (motion) => {
              motion.classList.remove("animate-delay");
              await motion.initAnimateEffect();
            });
          _this.updateVideoControl();
        },
      },
    };

    base.effect = effect;
    base.speed = 400;
    return new Swiper(this.mainSlide, base);
  }

  loadSlideVideo(videoWrapper, lazy = false) {
    if (window.innerWidth > 787) {
      if (!lazy && videoWrapper.classList.contains("video-mobile")) return;
    } else if (!lazy && videoWrapper.classList.contains("video-desktop")) {
      return;
    }

    if (!videoWrapper.getAttribute("loaded") && videoWrapper.querySelector("template")) {
      const content = document.createElement("div");
      content.appendChild(
        videoWrapper.querySelector("template").content.firstElementChild.cloneNode(true),
      );
      videoWrapper.setAttribute("loaded", true);

      const deferredElement = videoWrapper.appendChild(content.querySelector("video"));
      const alt = deferredElement.getAttribute("alt");
      const img = deferredElement.querySelector("img");
      const video = videoWrapper.querySelector("video");
      video.preload = "metadata";

      this.waitForFirstFrame(video).then(() => {
        this.mainSlide.classList.add("is-ready");
      });

      if (alt && img) img.setAttribute("alt", alt);

      if (
        deferredElement.nodeName === "VIDEO" &&
        deferredElement.getAttribute("autoplay") &&
        this.dataset.videoUserPaused !== "true"
      ) {
        const playPromise = deferredElement.play();
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch((error) => {
            if (error?.name !== "AbortError") {
              console.warn("play failed:", error);
            }
          });
        }
      }

      this.updateVideoControl(video.paused);

      new IntersectionObserver(
        (entries) => {
          entries.forEach(({ target, isIntersecting, intersectionRatio }) => {
            if (isIntersecting && intersectionRatio > 0.2) {
              if (this.dataset.videoUserPaused === "true") {
                target.pause();
                this.updateVideoControl(true);
                return;
              }

              const playPromise = video.play();
              if (playPromise && typeof playPromise.catch === "function") {
                playPromise.catch((error) => {
                  if (error?.name !== "AbortError") {
                    console.warn("play failed:", error);
                  }
                });
              }
            } else {
              target.pause();
            }
          });
        },
        { threshold: [0, 0.2, 1] },
      ).observe(video);
    }
  }

  waitForFirstFrame(video) {
    return new Promise((resolve) => {
      const done = () => {
        cleanup();
        resolve();
      };
      const cleanup = () => {
        video.removeEventListener("loadeddata", done);
        video.removeEventListener("playing", done);
        video.removeEventListener("timeupdate", onTime);
      };
      const onTime = () => {
        if (video.currentTime > 0) done();
      };

      if ((video.readyState >= 2 && video.videoWidth) || video.currentTime > 0) {
        resolve();
        return;
      }

      video.addEventListener("loadeddata", done, { once: true });
      video.addEventListener("playing", done, { once: true });
      video.addEventListener("timeupdate", onTime, { once: true });

      if ("requestVideoFrameCallback" in video) {
        video.requestVideoFrameCallback(done);
      }
    });
  }
}

if (!customElements.get("slideshow-section")) {
  customElements.define("slideshow-section", SlideshowSection);
}
